// Targeted Count Setup - Choose What to Count
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ArrowLeft,
  ArrowRight,
  Target,
  Info,
  Calendar,
  MapPin,
  User,
  FileText,
  Package,
  MapPinned,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { format } from "date-fns";

export function StockTakesTargetedCountSetupPage() {
  const { navigateTo } = useNavigation();
  const [countMode, setCountMode] = useState<"items" | "location">("items");
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    warehouse: "",
    assignedTo: "",
    description: `Targeted Count - ${format(new Date(), "MMM dd, yyyy")}`,
    notes: "",
  });

  const handleNext = () => {
    // Validation - only date is required
    if (!formData.date) {
      toast.error("Please select a date");
      return;
    }

    // Navigate to item selection page
    navigateTo("stock-takes/targeted-count/select-items", { 
      formData,
      countMode,
    });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] max-w-4xl mx-auto">
      {/* Fixed Header */}
      <div className="flex-shrink-0 pb-4 border-b bg-background">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateTo("stock-takes-home")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
                <h1 className="text-3xl font-bold tracking-tight">Targeted Count</h1>
                <Badge variant="secondary">Most Popular</Badge>
              </div>
              <p className="text-muted-foreground mt-1">
                Count specific items or locations you choose
              </p>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
              1
            </div>
            <span className="text-sm font-medium">Setup</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              2
            </div>
            <span className="text-sm text-muted-foreground">Select Items</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              3
            </div>
            <span className="text-sm text-muted-foreground">Count</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              4
            </div>
            <span className="text-sm text-muted-foreground">Review</span>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto py-6 space-y-6">
        {/* Info Banner */}
      <Card className="mb-6 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                Targeted Count gives you full control
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Choose exactly which items or locations to count. Perfect for spot checks,
                investigating discrepancies, or counting high-value items.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Count Mode Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>What do you want to count?</CardTitle>
          <CardDescription>
            Choose whether to select specific items or count by location
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={countMode} onValueChange={(value: "items" | "location") => setCountMode(value)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Specific Items */}
              <label
                htmlFor="mode-items"
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  countMode === "items"
                    ? "border-green-600 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="items" id="mode-items" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="font-medium">Specific Items</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Manually select individual items to count. Filter by category, location, or search.
                  </p>
                </div>
              </label>

              {/* By Location */}
              <label
                htmlFor="mode-location"
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  countMode === "location"
                    ? "border-green-600 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="location" id="mode-location" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPinned className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="font-medium">By Location</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Select one or more locations and count all items in those locations.
                  </p>
                </div>
              </label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Setup Form */}
      <Card className="flex-1">
        <CardHeader>
          <CardTitle>Count Details</CardTitle>
          <CardDescription>
            Provide the basic information for this inventory count
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Count Date *
            </Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>

          {/* Warehouse */}
          <div className="space-y-2">
            <Label htmlFor="warehouse" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Warehouse
            </Label>
            <Select
              value={formData.warehouse}
              onValueChange={(value) => setFormData({ ...formData, warehouse: value })}
            >
              <SelectTrigger id="warehouse">
                <SelectValue placeholder="Select warehouse (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-warehouse">No Warehouse / All Inventory</SelectItem>
                {mockWarehouses.map((warehouse) => (
                  <SelectItem key={warehouse.code} value={warehouse.code}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {formData.warehouse === "no-warehouse" || !formData.warehouse
                ? "Items from all locations will be available"
                : "Only items from this warehouse will be available"}
            </p>
          </div>

          {/* Assigned To */}
          <div className="space-y-2">
            <Label htmlFor="assignedTo" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Assigned To
            </Label>
            <Select
              value={formData.assignedTo}
              onValueChange={(value) => setFormData({ ...formData, assignedTo: value })}
            >
              <SelectTrigger id="assignedTo">
                <SelectValue placeholder="Select person (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="john-smith">John Smith</SelectItem>
                <SelectItem value="jane-doe">Jane Doe</SelectItem>
                <SelectItem value="mike-johnson">Mike Johnson</SelectItem>
                <SelectItem value="sarah-williams">Sarah Williams</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Optional: Assign this count to a specific person
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Description
            </Label>
            <Input
              id="description"
              placeholder="e.g., High-Value Items Spot Check"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any special instructions or notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Fixed Action Bar */}
      <div className="flex-shrink-0 border-t bg-background shadow-lg p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigateTo("stock-takes-home")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button onClick={handleNext}>
            Next: Select Items
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
